import { useMemo, useState } from "react";
import ScrollArea from "../ScrollArea.jsx";
import PageHero from "../PageHero.jsx";
import Sheet from "../Sheet.jsx";
import TraceTable from "../TraceTable.jsx";
import { CompositionPanel, COMPOSITION_ROWS } from "./Composition.jsx";
import { useQuery } from "../../lib/dataClient.js";
import { NOTE_CLASS } from "../../lib/ui.js";
import { formatCompact } from "../../lib/renderers.js";
import { groupByName } from "../skills/grouping.js";
import {
  extractServerFromToolName,
  shortToolName,
  labelForMcpServer,
  mcpServerProvenance,
} from "../../lib/mcpServers.js";

const TRACE_LIMIT = 200;
const normalize = (s) => String(s || "").replace(/\s+/g, " ").trim();

// The mcp_server kernel emits each surface tool's input schema as a `schema_text`
// string (the contract's flattened form); re-parse it to the object the tool-detail
// panel renders. "" / unparseable → null (matches the prior runMcpServerExtraction).
function parseSchemaText(text) {
  if (typeof text !== "string" || !text) return null;
  try { return JSON.parse(text); } catch { return null; }
}

function FilterChip({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-[2px] border px-[6px] py-[2px] text-[10px] leading-[1.2] tracking-[0.04em] uppercase transition-colors cursor-pointer",
        active
          ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)]"
          : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)]",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

// Per-MCP-server VALUE lens. An MCP server is a BUNDLE — a set of tools, a
// declared capability surface, and a provenance — so this page fuses the two
// shipped detail templates:
//   • the SKILL drift template (SkillDetailBody): scan a scrubbable window of
//     the server's scoped prompts, extract the server's tool-definition
//     surface from each request, group identical surfaces into variants
//     (>1 = drift — a tool added/removed or a description/schema changed,
//     i.e. version skew or tool-poisoning), and
//   • the CLUSTER composition template (ClusterAnatomyBody): who actually
//     calls this server, with a concentration verdict (one developer's setup
//     vs org-wide adoption).
// The "Capability surface" roster joins the scanned definitions (what the
// server CLAIMS it can do, with descriptions) to the per-tool call counts
// (what was actually used), so a tool offered-but-never-called and a
// tool-called-without-a-sampled-definition are both visible. A row opens the
// tool's full definition (description + input schema) in the right panel.
export default function McpServerDetailBody({ nav, slot, ctx }) {
  const scopePath = nav?.scopePath || [];
  const terminal = scopePath.length ? scopePath[scopePath.length - 1] : null;
  const server = terminal?.dim === "mcp_servers" ? (terminal.ids?.[0] ?? terminal.id ?? null) : null;
  const serverLabel = server ? labelForMcpServer(server) : "";
  const range = ctx?.range;

  // ── Capability surface — READ the `mcp_server` signal_tags the backend pipeline
  // emits per offering prompt (value = JSON {surface, instructions, fingerprint}),
  // instead of hydrating a window and re-extracting in-browser. The kernel is the
  // same byte-portable mcpServerExtractor (parity-tested); the view is now driven
  // by the materialized annotation.
  const { data: surfaceData, loading: surfaceLoading } = useQuery(
    "getSignalTagValues",
    { namespace: "mcp_server", tag: server, range, scopePath },
    { enabled: Boolean(server) },
  );
  const found = useMemo(() => {
    const out = [];
    for (const r of surfaceData?.rows ?? []) {
      let d;
      try { d = JSON.parse(r.value); } catch { continue; }
      const surface = Array.isArray(d.surface) && d.surface.length
        ? d.surface.map((s) => ({ ...s, schema: parseSchemaText(s.schema_text) }))
        : null;
      const instructions = d.instructions || null;
      if (surface || instructions) out.push({ eventId: r.event_id, surface, instructions, fingerprint: d.fingerprint || null });
    }
    return out;
  }, [surfaceData]);

  // Surface variants, most-common first → v1 is the surface shown by default.
  // Grouped by the kernel-emitted fingerprint (the drift key); this cross-event
  // reduction is what the per-event kernel can't do.
  const versions = useMemo(
    () => groupByName(found.filter((r) => r.surface), (r) => r.fingerprint)
      .map((g) => ({ count: g.rows.length, surface: g.rows[0].surface }))
      .sort((a, b) => b.count - a.count)
      .map((v, i) => ({ ...v, n: i + 1 })),
    [found],
  );
  const [versionSel, setVersionSel] = useState(null);
  const activeVersion = versions.find((v) => v.n === versionSel) || versions[0] || null;
  const displaySurface = activeVersion?.surface || [];

  // Server instruction-block variants (drift), same shape as the surface.
  const instrVersions = useMemo(
    () => groupByName(found.filter((r) => r.instructions), (r) => normalize(r.instructions))
      .map((g) => ({ count: g.rows.length, text: g.rows[0].instructions }))
      .sort((a, b) => b.count - a.count)
      .map((v, i) => ({ ...v, n: i + 1 })),
    [found],
  );
  const [instrSel, setInstrSel] = useState(null);
  const activeInstr = instrVersions.find((v) => v.n === instrSel) || instrVersions[0] || null;

  // ── Recent calls + scope total — prompts that CALLED this server (the
  // mcp_servers scope), server-paginated. (No client window scrubber: the
  // surface is read from signal_tags above, not scanned over this window.)
  const { data: traces = [], loading: tracesLoading } = useQuery(
    "getTraces",
    { scopePath, range, limit: TRACE_LIMIT, sort: "newest" },
    { enabled: Boolean(server) },
  );
  const scopeTotal = Number(traces?.total) || traces.length || 0;

  // ── Usage — per-tool call counts within this server's scope. The "tools"
  // dim ∩ the mcp_servers scope; filter to this server's tools (the scope's
  // prompts may also call non-server tools, which this dim returns too).
  const { data: toolsData } = useQuery(
    "getDimensionEntries",
    { dimKey: "tools", parentScopePath: scopePath, limit: 200, range },
    { enabled: Boolean(server) },
  );
  const usageByName = useMemo(() => {
    const m = new Map();
    for (const e of toolsData?.entries ?? []) {
      if (extractServerFromToolName(e.id) !== server) continue;
      m.set(e.id, (m.get(e.id) || 0) + (Number(e.activity) || 0));
    }
    return m;
  }, [toolsData, server]);

  // Roster = scanned definitions ⨝ usage counts. Tools called but not in the
  // sampled surface still list (so actual usage is never hidden), flagged
  // unsampled. Most-used first.
  const roster = useMemo(() => {
    const byName = new Map();
    for (const t of displaySurface) byName.set(t.name, { ...t, calls: usageByName.get(t.name) || 0, sampled: true });
    for (const [name, calls] of usageByName) {
      if (!byName.has(name)) byName.set(name, { name, tool: shortToolName(name), description: "", schema: null, calls, sampled: false });
    }
    return [...byName.values()].sort((a, b) => (b.calls - a.calls) || a.tool.localeCompare(b.tool));
  }, [displaySurface, usageByName]);

  function openInstructions(v) {
    ctx?.onOpenDetail?.({
      eyebrow: `${serverLabel} · instructions`,
      title: instrVersions.length > 1 ? `Instructions v${v.n} · ${v.count} prompt${v.count === 1 ? "" : "s"}` : "Server instructions",
      body: v.text,
      detailKey: `${server}:instr:v${v.n}`,
    });
    slot?.onExpandDetail?.();
  }

  function openTool(t) {
    const parts = [];
    parts.push(t.description || "No description provided in the sampled requests.");
    if (t.schema) parts.push(`Input schema:\n${JSON.stringify(t.schema, null, 2)}`);
    if (!t.sampled) parts.push("(This tool was called, but its definition is unavailable in this captured variant.)");
    ctx?.onOpenDetail?.({
      eyebrow: `${serverLabel} · tool`,
      title: t.tool,
      body: parts.join("\n\n"),
      detailKey: `${server}:${t.name}`,
    });
    slot?.onExpandDetail?.();
  }

  // ── Composition — who calls this server (cluster template).
  const compEnabled = Boolean(server);
  const { data: epData, loading: epLoading } = useQuery("getDimensionEntries", {
    dimKey: "endpoints", parentScopePath: scopePath, limit: COMPOSITION_ROWS, range,
  }, { enabled: compEnabled });
  const { data: idData, loading: idLoading } = useQuery("getDimensionEntries", {
    dimKey: "identities", parentScopePath: scopePath, limit: COMPOSITION_ROWS, range,
  }, { enabled: compEnabled });
  const { data: agData, loading: agLoading } = useQuery("getDimensionEntries", {
    dimKey: "agents", parentScopePath: scopePath, limit: COMPOSITION_ROWS, range,
  }, { enabled: compEnabled });

  const concentration = useMemo(() => {
    const top = epData?.entries?.[0];
    const distinct = epData?.total ?? 0;
    if (!top || scopeTotal <= 0) return null;
    const share = Math.min(100, Math.round((top.activity / scopeTotal) * 100));
    if (share >= 60) {
      return `Concentrated. ${top.label} drives about ${share}% of calls to this server, likely from one developer's setup.`;
    }
    if (share >= 25) {
      return `Led by ${top.label} (~${share}%), with ${Math.max(0, distinct - 1)} other endpoint${distinct === 2 ? "" : "s"} calling it.`;
    }
    return `Diffuse. ${distinct} endpoints call this server with no single driver, suggesting organization-wide adoption.`;
  }, [epData, scopeTotal]);

  const drillInto = (dim) => (id) =>
    ctx?.onNavigatePath?.([...scopePath, { dim, ids: [String(id)], id: String(id) }]);
  const onSelectTrace = (id) => { ctx?.traceLog?.onSelectTrace?.(id); slot?.onExpandDetail?.(); };

  if (!server) {
    return (
      <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid gap-[12px] pb-[18px]">
        <PageHero eyebrow="MCP server" title="No server selected" />
      </ScrollArea>
    );
  }

  const driftNote = versions.length > 1 ? "definitions differ" : "stable definition";
  const status = surfaceLoading
    ? "reading capability surface…"
    : found.length === 0
      ? "No captured surface for this server in scope yet."
      : `${roster.length} tool${roster.length === 1 ? "" : "s"} · ${versions.length} surface variant${versions.length === 1 ? "" : "s"}${versions.length > 1 ? " (drift)" : ""}`;

  return (
    <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid gap-[12px] pb-[18px]">
      <PageHero
        eyebrow="MCP server"
        title={serverLabel}
        detail={`${mcpServerProvenance(server)} · derived from mcp__${server}__* tool calls`}
        metrics={[
          { label: "Prompts", value: scopeTotal ? formatCompact(scopeTotal) : "–", note: "called this server" },
          { label: "Tools", value: roster.length ? String(roster.length) : "–", note: "distinct tools" },
          { label: "Endpoints", value: epData?.total ? formatCompact(epData.total) : "–", note: "calling this server" },
          { label: "Surface variants", value: versions.length ? String(versions.length) : "–", note: driftNote },
        ]}
      />
      <span className={NOTE_CLASS}>{status}</span>

      {/* Capability surface + drift — what the server declares it can do, with
          per-tool usage. Variant chips switch which sampled surface is shown. */}
      <Sheet
        eyebrow="Definitions"
        title="Capability surface"
        detail="Tools this server offered to the model. Multiple variants indicate definition changes across prompts. Select a tool to view its full definition."
        tight
      >
        {versions.length > 1 ? (
          <div className="flex flex-wrap items-center gap-[4px] pb-[6px]">
            {versions.map((v) => (
              <FilterChip
                key={v.n}
                label={`surface v${v.n} · ${v.count}`}
                active={activeVersion?.n === v.n}
                onClick={() => setVersionSel(v.n)}
              />
            ))}
          </div>
        ) : null}
        {roster.length ? (
          <div className="grid">
            {roster.map((t) => (
              <button
                key={t.name}
                type="button"
                onClick={() => openTool(t)}
                className="grid w-full gap-[2px] border-t border-[color:var(--line-soft)] px-[2px] py-[6px] text-left first:border-t-0 cursor-pointer hover:bg-[color:var(--control-bg)]"
              >
                <span className="flex items-baseline justify-between gap-[8px]">
                  <span className="min-w-0 truncate font-mono text-[12px] leading-[1.2] text-[color:var(--ink)]">{t.tool}</span>
                  <span className="shrink-0 text-[10.5px] tabular-nums text-[color:var(--muted)]">
                    {t.calls ? `${formatCompact(t.calls)} called` : (t.sampled ? "offered" : "–")}
                  </span>
                </span>
                <span className={`${NOTE_CLASS} truncate`}>
                  {t.description || (t.sampled ? "no description provided" : "definition not in this surface variant")}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <span className={`${NOTE_CLASS} block py-[6px]`}>
            {surfaceLoading ? "reading…" : "No tool definitions captured for this server in scope."}
          </span>
        )}
      </Sheet>

      {/* Server instruction block — the server-level prose injected into the
          system prompt, when the upstream client included one. Best-effort
          (see extractServerInstructions); the panel only appears when found.
          Variant chips flag drift in the injected instructions. */}
      {activeInstr ? (
        <Sheet
          eyebrow="Instructions"
          title="Server instructions"
          detail="Server-level instructions found in the system prompt (best-effort extraction). More than one variant means the injected instructions changed across prompts."
          tight
        >
          {instrVersions.length > 1 ? (
            <div className="flex flex-wrap items-center gap-[4px] pb-[6px]">
              {instrVersions.map((v) => (
                <FilterChip
                  key={v.n}
                  label={`v${v.n} · ${v.count}`}
                  active={activeInstr?.n === v.n}
                  onClick={() => setInstrSel(v.n)}
                />
              ))}
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => openInstructions(activeInstr)}
            aria-label="Open full instructions in the detail panel"
            className="block w-full max-h-[260px] overflow-auto whitespace-pre-wrap break-words border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] p-[8px] text-left font-mono text-[11.5px] leading-[1.5] text-[color:var(--ink)] cursor-pointer hover:border-[color:var(--line-strong)]"
          >
            {activeInstr.text}
          </button>
        </Sheet>
      ) : null}

      {/* Composition — who calls this server. Concentration verdict up front,
          panels as evidence; each row narrows the scope to server ∩ member. */}
      <Sheet eyebrow="Composition" title="Who uses this server" tight>
        {concentration ? (
          <span className="block pb-[6px] text-[12px] leading-[1.45] text-[color:var(--muted)]">{concentration}</span>
        ) : null}
        <div className="grid gap-x-[18px] gap-y-[14px] [grid-template-columns:repeat(3,minmax(0,1fr))]">
          <CompositionPanel title="Endpoints" data={epData} loading={epLoading} scopeTotal={scopeTotal} onDrill={drillInto("endpoints")} />
          <CompositionPanel title="Identities" data={idData} loading={idLoading} scopeTotal={scopeTotal} onDrill={drillInto("identities")} />
          <CompositionPanel title="Agents" data={agData} loading={agLoading} scopeTotal={scopeTotal} onDrill={drillInto("agents")} />
        </div>
      </Sheet>

      {/* Recent calls — prompts that hit this server, server-paginated. */}
      <TraceTable
        rows={traces}
        selectedTraceId={ctx?.traceLog?.selectedTraceId || ""}
        onSelectTrace={onSelectTrace}
        showEndpoint={ctx?.traceLog?.showEndpoint}
        loading={tracesLoading}
        emptyMessage="No calls to this server in scope."
      />
    </ScrollArea>
  );
}
